/*
 * newsletter-popup.js — behaviour for sections/newsletter-popup.liquid
 *
 * Replaces the Ecomsend app. Read the header comment in the section file
 * before changing anything here, especially the consent rules.
 *
 * The short version: this popup does not appear until the visitor has answered
 * the cookie banner, and it only writes a cookie if they said yes. That is what
 * stops it stacking on the banner (F-CRO-07) and what keeps the cookie policy's
 * "functional cookies — only with your consent" line true (F-SEC-07).
 *
 * Depends on trapFocus / removeTrapFocus from assets/global.js, which is loaded
 * with `defer` on every page by layout/theme.liquid.
 */

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.newsletter-popup__dialog');
    this.form = this.querySelector('form');
    this.capturePanel = this.querySelector('[data-np-panel="capture"]');
    this.successPanel = this.querySelector('[data-np-panel="success"]');
    this.errorEl = this.querySelector('[data-np-error]');
    this.submitButton = this.querySelector('[data-np-submit]');
    this.storageKey = this.dataset.storageKey || 'hg_newsletter_popup';
    this.frequencyDays = parseInt(this.dataset.frequencyDays, 10) || 30;
    this.isOpen = false;
    this.openerElement = null;
    this.cleanupFns = [];

    this.querySelectorAll('[data-np-close]').forEach((el) =>
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.close('dismissed');
      })
    );

    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

    const copyButton = this.querySelector('[data-np-copy]');
    if (copyButton) copyButton.addEventListener('click', this.onCopy.bind(this));

    this.onKeydown = this.onKeydown.bind(this);

    // The theme editor should always show it, immediately, with no gating.
    if (this.dataset.designMode === 'true') {
      this.open();
      return;
    }

    if (this.shouldSkip()) return;

    this.whenConsentDecided()
      .then((decided) => {
        if (!decided) return; // visitor never answered; stay silent
        this.arm();
      })
      .catch(() => {
        /* never block the storefront on our own failure */
      });
  }

  disconnectedCallback() {
    this.teardownTriggers();
  }

  /* ---------------------------------------------------------------- consent */

  /*
   * Resolves true once it is safe to arm the triggers.
   *
   * Default: waits for an actual consent decision. Note that DECLINING counts —
   * a visitor who rejects cookies still sees the popup. The only group excluded
   * is those who never answer the banner at all.
   *
   * With `show_before_consent` on, undecided visitors are included too. We still
   * hold off while Shopify's banner is physically on screen, because it renders
   * at z-index 2000000 and would sit on top of this popup; but that hold is
   * capped, so an ignored banner does not suppress the popup forever.
   */
  whenConsentDecided() {
    if (this.dataset.showBeforeConsent === 'true') {
      return this.whenBannerClear();
    }

    return new Promise((resolve) => {
      const evaluate = () => {
        const state = this.consentState();
        if (state === 'decided') return resolve(true);
        if (state === 'no-banner') return resolve(true); // no banner is shown here
        return null; // undecided — keep waiting
      };

      const loadApi = (cb) => {
        if (window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.currentVisitorConsent) {
          cb();
        } else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], cb);
        } else {
          // No consent framework at all on this storefront — nothing to wait for.
          resolve(true);
        }
      };

      loadApi(() => {
        if (evaluate() !== null) return;

        // Undecided: wait for the banner to be answered. Shopify fires this
        // on the document when the visitor makes or changes a choice.
        const onCollected = () => {
          document.removeEventListener('visitorConsentCollected', onCollected);
          resolve(true);
        };
        document.addEventListener('visitorConsentCollected', onCollected);
        this.cleanupFns.push(() => document.removeEventListener('visitorConsentCollected', onCollected));
      });
    });
  }

  /*
   * Resolves once Shopify's consent banner is not covering the screen, or after
   * `bannerWaitMs`, whichever comes first. Used only when the merchant has opted
   * to show the popup before a consent decision exists.
   */
  whenBannerClear() {
    const CAP_MS = 20000;
    const isUp = () => {
      const el = document.getElementById('shopify-pc__banner');
      if (!el) return false;
      // offsetParent is always null for position:fixed, so it cannot be used here.
      if (typeof el.checkVisibility === 'function') return el.checkVisibility();
      return el.getBoundingClientRect().height > 0;
    };

    if (!isUp()) return Promise.resolve(true);

    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearInterval(poll);
        clearTimeout(cap);
        document.removeEventListener('visitorConsentCollected', finish);
        resolve(true);
      };
      const poll = setInterval(() => {
        if (!isUp()) finish();
      }, 500);
      const cap = setTimeout(finish, CAP_MS);
      document.addEventListener('visitorConsentCollected', finish);
      this.cleanupFns.push(finish);
    });
  }

  consentState() {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.currentVisitorConsent !== 'function') return 'no-banner';

    const consent = cp.currentVisitorConsent() || {};
    const answered = ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(
      (key) => consent[key] !== '' && consent[key] != null
    );
    if (answered) return 'decided';

    // Nothing recorded. If no banner will ever be shown in this region there is
    // nothing to wait for; otherwise stay quiet until the visitor answers.
    try {
      if (typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner() === false) return 'no-banner';
    } catch (e) {
      /* fall through */
    }
    return 'undecided';
  }

  /* Storage is allowed only when the visitor has actively allowed something. */
  mayPersist() {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return false;
    try {
      if (typeof cp.preferencesProcessingAllowed === 'function' && cp.preferencesProcessingAllowed()) return true;
      if (typeof cp.marketingAllowed === 'function' && cp.marketingAllowed()) return true;
    } catch (e) {
      /* treat as not allowed */
    }
    return false;
  }

  /* ---------------------------------------------------------------- storage */

  readState() {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.indexOf(`${this.storageKey}=`) === 0);
    if (cookie) return cookie.split('=')[1];

    try {
      return window.sessionStorage.getItem(this.storageKey);
    } catch (e) {
      return null;
    }
  }

  writeState(value, days) {
    if (this.mayPersist()) {
      const expires = new Date(Date.now() + days * 86400000).toUTCString();
      document.cookie = `${this.storageKey}=${value}; expires=${expires}; path=/; SameSite=Lax${
        location.protocol === 'https:' ? '; Secure' : ''
      }`;
      return;
    }

    // Consent declined: session-scoped only, nothing survives the tab.
    try {
      window.sessionStorage.setItem(this.storageKey, value);
    } catch (e) {
      /* private mode — the popup simply reappears next page, which is safe */
    }
  }

  shouldSkip() {
    if (this.dataset.alreadySubscribed === 'true') return true;
    const state = this.readState();
    return state === 'subscribed' || state === 'dismissed';
  }

  /* --------------------------------------------------------------- triggers */

  arm() {
    if (this.shouldSkip()) return;

    const trigger = this.dataset.trigger || 'delay';
    const delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;

    if (trigger === 'scroll') {
      const depth = (parseInt(this.dataset.scrollDepth, 10) || 40) / 100;
      const onScroll = () => {
        const scrolled = window.scrollY + window.innerHeight;
        const total = document.documentElement.scrollHeight;
        if (total > 0 && scrolled / total >= depth) this.open();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      this.cleanupFns.push(() => window.removeEventListener('scroll', onScroll));
      onScroll();
      return;
    }

    if (trigger === 'exit') {
      // Exit intent is meaningless without a cursor, so touch devices fall back
      // to the delay instead of simply never seeing it.
      const isTouch = window.matchMedia('(hover: none)').matches;
      if (isTouch) {
        this.timer = setTimeout(() => this.open(), delay || 8000);
        this.cleanupFns.push(() => clearTimeout(this.timer));
        return;
      }
      const onLeave = (event) => {
        if (event.clientY <= 0) this.open();
      };
      document.addEventListener('mouseout', onLeave);
      this.cleanupFns.push(() => document.removeEventListener('mouseout', onLeave));
      return;
    }

    this.timer = setTimeout(() => this.open(), delay);
    this.cleanupFns.push(() => clearTimeout(this.timer));
  }

  teardownTriggers() {
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }

  /* ------------------------------------------------------------ open / close */

  open() {
    if (this.isOpen) return;
    if (this.dataset.designMode !== 'true' && this.shouldSkip()) return;

    this.teardownTriggers();
    this.isOpen = true;
    this.openerElement = document.activeElement;

    this.hidden = false;
    // Force a frame so the CSS transition has a starting point to animate from.
    requestAnimationFrame(() => this.classList.add('newsletter-popup--open'));

    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeydown);

    if (typeof trapFocus === 'function') {
      const field = this.querySelector('.newsletter-popup__input');
      trapFocus(this.dialog, field || this.dialog);
    }
  }

  close(reason) {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.classList.remove('newsletter-popup--open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeydown);

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openerElement);

    const finish = () => {
      this.hidden = true;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else setTimeout(finish, 250);

    if (reason && this.dataset.designMode !== 'true') {
      this.writeState(reason, reason === 'subscribed' ? 365 : this.frequencyDays);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close('dismissed');
    }
  }

  /* ------------------------------------------------------------------ submit */

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    this.setBusy(true);
    this.hideError();

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
        redirect: 'follow',
      });

      // Shopify redirects a successful customer form to ?customer_posted=true.
      // That redirect is the only reliable success signal available here.
      if (response.ok && response.url && response.url.indexOf('customer_posted=true') !== -1) {
        this.showSuccess();
      } else {
        this.showError();
      }
    } catch (e) {
      // Network or CORS failure — fall back to a normal form post so the
      // visitor's signup is never silently lost.
      this.form.submit();
      return;
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy;
    this.submitButton.classList.toggle('newsletter-popup__button--busy', busy);
    const spinner = this.submitButton.querySelector('.newsletter-popup__spinner');
    if (spinner) spinner.hidden = !busy;
  }

  showError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    const field = this.querySelector('.newsletter-popup__input');
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      field.focus();
    }
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    const field = this.querySelector('.newsletter-popup__input');
    if (field) field.removeAttribute('aria-invalid');
  }

  showSuccess() {
    this.writeState('subscribed', 365);
    if (this.capturePanel) this.capturePanel.hidden = true;
    if (this.successPanel) {
      this.successPanel.hidden = false;
      const heading = this.successPanel.querySelector('[data-np-success-heading]');
      if (heading) heading.focus();
      if (typeof trapFocus === 'function') trapFocus(this.dialog, heading || this.dialog);
    }
  }

  /* -------------------------------------------------------------------- copy */

  async onCopy() {
    const input = this.querySelector('[data-np-code]');
    const feedback = this.querySelector('[data-np-copied]');
    if (!input) return;

    let copied = false;
    try {
      await navigator.clipboard.writeText(input.value);
      copied = true;
    } catch (e) {
      // Older browsers, or clipboard permission denied.
      input.removeAttribute('readonly');
      input.select();
      try {
        copied = document.execCommand('copy');
      } catch (err) {
        copied = false;
      }
      input.setAttribute('readonly', 'readonly');
    }

    if (feedback) {
      feedback.textContent = copied
        ? feedback.dataset.copied || 'Copied'
        : feedback.dataset.failed || '';
      if (copied) {
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          feedback.textContent = '';
        }, 2500);
      }
    }
    if (!copied) input.select();
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
